import React, { FC, useEffect } from 'react';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  FormHelperText,
  Divider,
  Typography,
} from '@mui/material';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { z, ZodType } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TFeeComponent,
  TStudentFees,
} from '../../../../../apis/admissions/adminssionFees.interfaces';
import {
  TStatus,
  TPaymentMode,
} from '../../../../../apis/finance/finance.interfaces';
import { useTranslation } from '../../../../../hooks/useTranslationHook';
import {
  isValidIsoDateString,
  zNumberFromInput,
  zYearFromInput,
} from 'src/utils/zodFormHelpers';

// Validation schemas for nested objects
const feeComponentSchema = z
  .object({
    id: z.string().optional(),
    componentId: z.string().min(1, 'Component ID is required'),
    componentName: z.string().min(1, 'Component name is required'),
    description: z.string().min(1, 'Description is required'),
    amount: zNumberFromInput({
      min: 0,
      message: 'Amount must be positive',
    }),
    dueDate: z
      .union([z.string(), z.null()])
      .transform((val) => val ?? '')
      .refine((val) => val === '' || isValidIsoDateString(val), {
        message: 'Enter a valid date',
      }),
    status: z.nativeEnum(TStatus),
    paidAmount: zNumberFromInput({
      min: 0,
      message: 'Paid amount must be positive',
    }),
    createdBy: z.string().min(1, 'Created by is required'),
    instituteRef: z.string().min(1, 'Institute reference is required'),
    purpose: z.string().min(1, 'Purpose is required'),
    academicYear: zYearFromInput({
      message: 'Academic year must be positive',
    }),
    isOptedOut: z.boolean().optional(),
  })
  .refine(
    (data) => {
      return data.paidAmount <= data.amount;
    },
    {
      message: 'Paid amount cannot exceed the total amount',
      path: ['paidAmount'], // This will make the error show up on the paidAmount field
    }
  );

const paymentHistorySchema = z.object({
  paymentDate: z.coerce
    .date()
    .refine((d) => !Number.isNaN(d.getTime()), { message: 'Enter a valid date' }),
  paymentMode: z.nativeEnum(TPaymentMode),
  amountPaid: zNumberFromInput({
    min: 0,
    message: 'Amount must be positive',
  }),
  referenceId: z.string().optional(),
});

const StudentFeesSchema = z.object({
  studentId: z.string().min(1, 'Student ID is required'),
  academicYear: z.string().min(1, 'Academic year is required'),
  totalFee: zNumberFromInput({
    min: 0,
    message: 'Total fee must be positive',
  }),
  feeComponents: z.array(feeComponentSchema),
  totalPaid: zNumberFromInput({
    min: 0,
    message: 'Total paid must be positive',
  }),
  outstandingBalance: zNumberFromInput(),
  paymentHistory: z.array(paymentHistorySchema),
  status: z.nativeEnum(TStatus),
  createdBy: z.string().min(1, 'Created by is required'),
  instituteRef: z.string().min(1, 'Institute reference is required'),
  purpose: z.string().min(1, 'Purpose is required'),
}) as ZodType<TStudentFees>;

interface StudentFeesFormProps {
  studentFees: TStudentFees | null;
  onSubmit: (data: TStudentFees) => void;
  submitRef: React.MutableRefObject<(() => void) | null>;
}

// Add this utility function at the top of your component
const formatDateForInput = (date: string): string => {
  if (!date) return '';
  return date.split('T')[0];
};

// Add this utility function to compute individual component status
const computeComponentStatus = (
  amount: number,
  paidAmount: number
): TStatus => {
  if (!amount || amount === 0) return TStatus.UNPAID;
  if (paidAmount === 0) return TStatus.UNPAID;
  if (paidAmount >= amount) return TStatus.PAID;
  return TStatus.PARTIAL;
};

// Update the overall status computation (excludes opted-out components)
const computeOverallStatus = (
  feeComponents: Array<{ amount: number; paidAmount: number; isOptedOut?: boolean }>
) => {
  const selected = feeComponents.filter((c) => !c.isOptedOut);
  if (!selected.length) return TStatus.UNPAID;

  const totalAmount = selected.reduce(
    (sum, comp) => sum + (comp.amount || 0),
    0
  );
  const totalPaid = selected.reduce(
    (sum, comp) => sum + (comp.paidAmount || 0),
    0
  );

  if (totalPaid === 0) return TStatus.UNPAID;
  if (totalPaid >= totalAmount) return TStatus.PAID;
  return TStatus.PARTIAL;
};

// Add this utility function to get the first error message for a row
const getFirstRowError = (errors: any, index: number): string | undefined => {
  const rowErrors = errors?.feeComponents?.[index];
  if (!rowErrors) return undefined;

  // Check each field in order of appearance
  const fields = ['componentName', 'amount', 'dueDate', 'paidAmount'];
  for (const field of fields) {
    if (rowErrors[field]?.message) {
      return rowErrors[field].message;
    }
  }
  return undefined;
};

const StudentFeesFormView: FC<StudentFeesFormProps> = ({
  studentFees,
  onSubmit,
  submitRef,
}) => {
  const { translateText } = useTranslation();
  const defaultValues: TStudentFees = {
    studentId: '',
    academicYear: '',
    totalFee: 0,
    feeComponents: [],
    totalPaid: 0,
    outstandingBalance: 0,
    paymentHistory: [],
    status: TStatus.UNPAID,
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
    control,
    setError,
    clearErrors,
  } = useForm<TStudentFees>({
    resolver: zodResolver(StudentFeesSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const { fields, append } = useFieldArray({
    control: control,
    name: 'feeComponents',
  });

  // Watch for changes in fee components
  const feeComponents = useWatch({
    control,
    name: 'feeComponents',
  });

  // Calculate totals whenever fee components change (exclude opted-out)
  useEffect(() => {
    if (!feeComponents) return;

    const selected = feeComponents.filter((c) => !c.isOptedOut);
    const totalFee = selected.reduce(
      (sum, component) => sum + (component.amount || 0),
      0
    );
    const totalPaidAmount = selected.reduce(
      (sum, component) => sum + (component.paidAmount || 0),
      0
    );

    setValue('totalFee', totalFee);
    setValue('totalPaid', totalPaidAmount);
    setValue('outstandingBalance', totalFee - totalPaidAmount);
  }, [feeComponents, setValue]);

  // Reset form when studentFees changes
  useEffect(() => {
    if (studentFees) {
      reset({
        ...studentFees,
        status: studentFees.status || TStatus.UNPAID,
      });
      // Explicitly set the status
      setValue('status', studentFees.status || TStatus.UNPAID, {
        shouldValidate: true,
      });
    }
  }, [studentFees, reset, setValue]);

  // Update statuses when amounts change
  useEffect(() => {
    if (!feeComponents) return;

    // Update individual component statuses
    feeComponents.forEach((component, index) => {
      const newStatus = computeComponentStatus(
        component.amount,
        component.paidAmount
      );
      setValue(`feeComponents.${index}.status`, newStatus);
    });

    // Update overall status
    const newOverallStatus = computeOverallStatus(feeComponents);
    setValue('status', newOverallStatus);
  }, [feeComponents, setValue]);

  // Store the submit function in the ref
  useEffect(() => {
    submitRef.current = handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit, submitRef]);

  return (
    <form style={{ width: '100%' }}>
      <Grid 
        container 
        spacing={{ xs: 1, sm: 2 }} 
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-fee-form-container"
      >
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={translateText('students.fees.studentId')}
            {...register('studentId')}
            error={!!errors.studentId}
            helperText={errors.studentId?.message}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', sm: '14px' },
                minHeight: { xs: '44px', sm: 'auto' },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={translateText('students.fees.academicYear')}
            {...register('academicYear')}
            error={!!errors.academicYear}
            helperText={errors.academicYear?.message}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', sm: '14px' },
                minHeight: { xs: '44px', sm: 'auto' },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label={translateText('students.fees.totalFee')}
            {...register('totalFee')}
            InputProps={{ readOnly: true }}
            helperText={translateText(
              'students.fees.computedFromFeeComponents'
            )}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', sm: '14px' },
                minHeight: { xs: '44px', sm: 'auto' },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label={translateText('students.fees.totalPaid')}
            {...register('totalPaid')}
            InputProps={{ readOnly: true }}
            helperText={translateText(
              'students.fees.computedFromFeeComponents'
            )}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', sm: '14px' },
                minHeight: { xs: '44px', sm: 'auto' },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label={translateText('students.fees.outstandingBalance')}
            {...register('outstandingBalance')}
            InputProps={{ readOnly: true }}
            helperText={translateText(
              'students.fees.computedFromFeeComponents'
            )}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', sm: '14px' },
                minHeight: { xs: '44px', sm: 'auto' },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel sx={{ fontSize: { xs: '16px', sm: '14px' } }}>
              {translateText('students.fees.overallStatus')}
            </InputLabel>
            <Select
              label={translateText('students.fees.overallStatus')}
              value={getValues('status')}
              inputProps={{ readOnly: true }}
              sx={{
                minHeight: { xs: '44px', sm: 'auto' },
                '& .MuiSelect-select': {
                  fontSize: { xs: '16px', sm: '14px' },
                },
              }}
            >
              {Object.values(TStatus).map((status) => (
                <MenuItem 
                  key={status} 
                  value={status}
                  sx={{
                    minHeight: { xs: '44px', sm: 'auto' },
                    fontSize: { xs: '16px', sm: '14px' },
                  }}
                >
                  {status.charAt(0).toUpperCase() +
                    status.slice(1).toLowerCase()}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              ({translateText('students.fees.computed')})
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Divider 
            sx={{ 
              width: '100%', 
              mt: { xs: 2, sm: 4 }, 
              mb: { xs: 1, sm: 2 } 
            }} 
            textAlign="left"
          >
            <Typography 
              variant="body1"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: { xs: 600, sm: 400 },
              }}
            >
              {translateText('students.fees.feeComponents')}
            </Typography>
          </Divider>
        </Grid>

        {fields.map((field, index) => {
          const isOptedOut = getValues(`feeComponents.${index}.isOptedOut`);
          return (
          <Grid item xs={12} key={field.id} sx={{ mb: { xs: 1.5, sm: 2 } }}>
            {isOptedOut && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                {translateText('students.fees.optedOut')}
              </Typography>
            )}
            <Grid container spacing={{ xs: 1, sm: 2 }} sx={isOptedOut ? { opacity: 0.7 } : {}}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={translateText('students.fees.componentName')}
                  {...register(`feeComponents.${index}.componentName`)}
                  InputProps={isOptedOut ? { readOnly: true } : undefined}
                  error={!!errors.feeComponents?.[index]?.componentName}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '16px', sm: '14px' },
                      minHeight: { xs: '44px', sm: 'auto' },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="number"
                  label={translateText('students.fees.amount')}
                  {...register(`feeComponents.${index}.amount`, {
                    valueAsNumber: true,
                    onChange: (e) => {
                      const amount = Number(e.target.value);
                      const paidAmount =
                        getValues(`feeComponents.${index}.paidAmount`) || 0;

                      if (paidAmount > amount) {
                        setValue(`feeComponents.${index}.paidAmount`, amount, {
                          shouldValidate: true,
                        });
                      }
                    },
                  })}
                  error={!!errors.feeComponents?.[index]?.amount}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '16px', sm: '14px' },
                      minHeight: { xs: '44px', sm: 'auto' },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  type="date"
                  label={translateText('students.fees.dueDate')}
                  InputLabelProps={{ shrink: true }}
                  {...register(`feeComponents.${index}.dueDate`, {
                    setValueAs: (value: string) => value || '',
                  })}
                  value={formatDateForInput(
                    getValues(`feeComponents.${index}.dueDate`) || ''
                  )}
                  onChange={(e) => {
                    setValue(
                      `feeComponents.${index}.dueDate`,
                      e.target.value || ''
                    );
                  }}
                  error={!!errors.feeComponents?.[index]?.dueDate}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '16px', sm: '14px' },
                      minHeight: { xs: '44px', sm: 'auto' },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="number"
                  label={translateText('students.fees.paidAmount')}
                  {...register(`feeComponents.${index}.paidAmount`, {
                    valueAsNumber: true,
                    onChange: (e) => {
                      const paidAmount = Number(e.target.value);
                      const amount =
                        getValues(`feeComponents.${index}.amount`) || 0;

                      if (paidAmount > amount) {
                        setError(`feeComponents.${index}.paidAmount`, {
                          type: 'manual',
                          message: 'Paid amount cannot exceed the total amount',
                        });
                      } else {
                        clearErrors(`feeComponents.${index}.paidAmount`);
                      }
                    },
                  })}
                  error={!!errors.feeComponents?.[index]?.paidAmount}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '16px', sm: '14px' },
                      minHeight: { xs: '44px', sm: 'auto' },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '16px', sm: '14px' } }}>
                    {translateText('common.status')}
                  </InputLabel>
                  <Select
                    label={translateText('common.status')}
                    value={getValues(`feeComponents.${index}.status`) || ''}
                    inputProps={{ readOnly: true }}
                    sx={{
                      minHeight: { xs: '44px', sm: 'auto' },
                      '& .MuiSelect-select': {
                        fontSize: { xs: '16px', sm: '14px' },
                      },
                    }}
                  >
                    {Object.values(TStatus).map((status) => (
                      <MenuItem 
                        key={status} 
                        value={status}
                        sx={{
                          minHeight: { xs: '44px', sm: 'auto' },
                          fontSize: { xs: '16px', sm: '14px' },
                        }}
                      >
                        {status.charAt(0).toUpperCase() +
                          status.slice(1).toLowerCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Error message row */}
            {getFirstRowError(errors, index) && (
              <Grid
                item
                xs={12}
                sx={{ pt: '0 !important', pb: 2 }}
                data-testid={`error-${index}`}
              >
                <FormHelperText error sx={{ ml: 2 }}>
                  {getFirstRowError(errors, index)}
                </FormHelperText>
              </Grid>
            )}
          </Grid>
          );
        })}

        <Grid item xs={12}>
          <Button
            variant="outlined"
            onClick={() =>
              append({
                id: '',
                componentId: '',
                componentName: '',
                description: '',
                amount: 0,
                dueDate: null,
                status: TStatus.UNPAID,
                paidAmount: 0,
                createdBy: '',
                instituteRef: '',
                purpose: '',
                academicYear: 0,
                isOptedOut: false,
              } as TFeeComponent)
            }
            sx={{
              minHeight: { xs: '44px', sm: '36px' },
              fontSize: { xs: '16px', sm: '14px' },
              width: { xs: '100%', sm: 'auto' },
            }}
            data-testid="add-fee-component-button"
          >
            {translateText('students.fees.addFeeComponent')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default StudentFeesFormView;
